import React, { useMemo } from 'react';
import { InputNumber, Select, Avatar, Tooltip, Input } from 'antd';
import type { EncounterCreature, Monster, NPC, Image, CustomFormFieldComponentProps } from '../../types/types';
import { useImageLibraryStore } from '../../state/createImageLibraryStore';
import { useSessionData } from '../../hooks/useSessionData';
import EditableListManager from '../../components/sheet/EditableListManager';
import styles from './EncounterCreatureFields.module.css';

const EncounterCreatureFields: React.FC<CustomFormFieldComponentProps> = ({ internalState, setInternalState }) => {
  // Use useSessionData to get stable references to the rosters, preventing unnecessary re-renders.
  const monsterRoster = useSessionData(session => session.cache.monsterRoster, []);
  const npcs = useSessionData(session => session.roster.npcs, []);
  const imageLibrary = useImageLibraryStore(state => state.images);

  const creatureOptions = useMemo(() => {
    const allCreatures = [
      ...(monsterRoster ?? []).map((m: Monster) => ({ ...m, type: 'monster' as const })),
      ...(npcs ?? []).map((n: NPC) => ({ ...n, type: 'npc' as const }))
    ];
    return (allCreatures ?? []).sort((a, b) => a.name.localeCompare(b.name));
  }, [monsterRoster, npcs]);

  const imageMap = useMemo(() => new Map((imageLibrary ?? []).map((img: Image) => [img.id, img.imageUrl])), [imageLibrary]);

  return (
    <EditableListManager<EncounterCreature>
      items={internalState?.creatures ?? []}
      onChange={(creaturesUpdater) => {
        setInternalState(prevState => ({
          ...prevState,
          // The 'creaturesUpdater' can be either the new array directly
          // or a function to update the previous array. We need to handle both cases.
          creatures: typeof creaturesUpdater === 'function'
            ? creaturesUpdater(prevState.creatures ?? [])
            : creaturesUpdater,
        }));
      }}
      defaultNewItem={() => ({ encounterId: 0, creatureId: null, creatureType: 'monster', quantity: '1', chance: 100 } as unknown as Omit<EncounterCreature, 'id'>)}
      addText="Add Creature"
      renderItemFields={(item, index, handleItemChange) => {
        const creatureDetails = (creatureOptions ?? []).find(c => c.id === item.creatureId);
        return (
          <>
            <Avatar src={creatureDetails?.imageId ? imageMap.get(creatureDetails.imageId) as string : undefined}>
              {creatureDetails?.name?.charAt(0) ?? '?'}
            </Avatar>
            <Select
              showSearch
              placeholder="Select a creature"
              value={item.creatureId || undefined}
              onChange={(value) => {
                const creatureType = (creatureOptions ?? []).find(c => c.id === value)?.type ?? 'monster';
                handleItemChange(index, { creatureId: value, creatureType: creatureType });
              }}
              className={styles.creatureSelect}
              optionFilterProp="children"
            >
              {(creatureOptions ?? []).map(c => <Select.Option key={c.id} value={c.id}>{c.name} ({c.type})</Select.Option>)}
            </Select>
            <Tooltip title="Quantity (e.g., 1, 1d4, 2d6+1)">
              <Input value={item.quantity} onChange={(e) => handleItemChange(index, { quantity: e.target.value || '1' })} style={{ width: 80 }} />
            </Tooltip>
            <Tooltip title="Chance %">
              <InputNumber
                min={0} max={100} value={item.chance}
                onChange={(val) => handleItemChange(index, { chance: val ?? 0 })}
                style={{ width: 70 }}
                formatter={value => `${value}%`}
                parser={value => value?.replace('%', '') as unknown as number}
              />
            </Tooltip>
          </>
        );
      }}
    />
  );
};

export default EncounterCreatureFields;